import { z } from 'zod';
import { isoTimestamp } from './common';
import { cronExpressionSchema, timeZoneSchema } from './cron';
import { requestContextValuesSchema } from './prompts';
import { resourceNameSchema } from './tools';

/**
 * Workflows, modelled on Mastra workflows (`@mastra/core/workflows`): a graph of entries run in
 * order. Leaf steps do the work (agent, tool, sandbox, transform, nested workflow, human input);
 * container entries shape the flow (sleep, parallel, conditional, loop, foreach).
 *
 * Data moves between steps through templates: `{{input.x}}` (workflow input), `{{context.x}}`
 * (request context), `{{prev}}` (the previous entry's output), `{{steps.<id>.output.a}}`,
 * `{{item}}` / `{{index}}` (inside foreach) and `{{iteration}}` (inside loops).
 */

// --- Shared pieces ---

export const workflowStepIdSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]{0,39}$/, 'Step ids are 1–40 lowercase letters, digits or _');

const workflowTemplateSchema = z.string().max(50_000);
const stepName = z.string().trim().min(1, 'Name the step').max(100);
const stepDescription = z.string().trim().max(500).optional();

export const CONDITION_OPERATORS = [
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'is_empty',
  'not_empty',
  'greater_than',
  'greater_or_equal',
  'less_than',
  'less_or_equal',
] as const;
export const conditionOperatorSchema = z.enum(CONDITION_OPERATORS);
export type ConditionOperator = z.infer<typeof conditionOperatorSchema>;

/** One comparison: a value (template) against another. */
export const conditionRuleSchema = z
  .object({
    left: workflowTemplateSchema,
    operator: conditionOperatorSchema,
    right: workflowTemplateSchema.default(''),
  })
  .strict();
export type ConditionRule = z.infer<typeof conditionRuleSchema>;
/** Kept for older definitions: a single rule. */
export const stepConditionSchema = conditionRuleSchema;
export type StepCondition = ConditionRule;

/** Rules combined with all (and) / any (or) — Mastra's declarative predicates, in a form editor. */
export const conditionSchema = z.preprocess(
  (value) =>
    value && typeof value === 'object' && 'operator' in value
      ? { match: 'all', rules: [value] }
      : value,
  z
    .object({
      match: z.enum(['all', 'any']).default('all'),
      rules: z.array(conditionRuleSchema).min(1, 'Add a rule').max(10),
    })
    .strict(),
);
export type Condition = z.infer<typeof conditionSchema>;

export const workflowInputSchema = z.object({
  name: z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/, 'Input names are identifiers'),
  type: z.enum(['string', 'number', 'boolean', 'json']).default('string'),
  required: z.boolean().default(true),
  description: z.string().max(500).default(''),
});
export type WorkflowInput = z.infer<typeof workflowInputSchema>;

// --- Leaf steps ---

const leafBase = {
  id: workflowStepIdSchema,
  name: stepName,
  description: stepDescription,
  /** Runs the step only when the condition holds (otherwise it is skipped). */
  when: conditionSchema.optional(),
};

const agentStep = z.object({
  ...leafBase,
  type: z.literal('agent'),
  agentId: z.uuid(),
  version: z.enum(['draft', 'published']).default('published'),
  /** The user message sent to the agent. */
  prompt: workflowTemplateSchema.min(1, 'Write the message for the agent'),
  /** `json`: the answer is parsed as JSON (later steps can read its fields). */
  output: z.enum(['text', 'json']).default('text'),
});
const toolStep = z.object({
  ...leafBase,
  type: z.literal('tool'),
  toolId: z.uuid(),
  /** Tool arguments; a value that is exactly one placeholder keeps its type. */
  args: z.record(z.string().min(1).max(100), workflowTemplateSchema).default({}),
});
const sandboxStep = z.object({
  ...leafBase,
  type: z.literal('sandbox'),
  sandboxId: z.uuid(),
  /**
   * A fixed program. Workflow values are never spliced into code (that would let input or agent
   * output inject code); the program reads them from `input.json`: `{ input, context, steps, prev, item }`.
   */
  code: z
    .string()
    .min(1, 'Write the code to run')
    .max(50_000)
    .refine((code) => templateRefs(code).length === 0, {
      message: 'Sandbox code cannot contain {{…}} placeholders; read values from input.json',
    }),
});
const transformStep = z.object({
  ...leafBase,
  type: z.literal('transform'),
  /** The step output; text that is valid JSON becomes structured output. */
  output: workflowTemplateSchema,
});
const nestedWorkflowStep = z.object({
  ...leafBase,
  type: z.literal('workflow'),
  workflowId: z.uuid(),
  /** Input of the nested workflow; a value that is exactly one placeholder keeps its type. */
  input: z.record(z.string().min(1).max(64), workflowTemplateSchema).default({}),
});
const humanStep = z.object({
  ...leafBase,
  type: z.literal('human'),
  /** Shown to whoever resumes the run (e.g. "Approve the refund for {{input.order}}"). */
  message: workflowTemplateSchema.min(1, 'Say what you need from the person'),
  /** The form they fill in; its values are the step output. */
  fields: z.array(workflowInputSchema).max(20).default([]),
});

/** Steps that can run inside containers (parallel, conditional, loop, foreach). */
export const workflowContainerStepSchema = z.discriminatedUnion('type', [
  agentStep,
  toolStep,
  sandboxStep,
  transformStep,
  nestedWorkflowStep,
]);
export type WorkflowContainerStep = z.infer<typeof workflowContainerStepSchema>;

/** Every leaf step (human steps only at the top level, where a run can suspend). */
export const workflowStepSchema = z.discriminatedUnion('type', [
  agentStep,
  toolStep,
  sandboxStep,
  transformStep,
  nestedWorkflowStep,
  humanStep,
]);
export type WorkflowStep = z.infer<typeof workflowStepSchema>;
export type WorkflowStepType = WorkflowStep['type'];

// --- Container entries ---

const containerBase = { id: workflowStepIdSchema, name: stepName, description: stepDescription };

const sleepEntry = z
  .object({
    ...containerBase,
    type: z.literal('sleep'),
    mode: z.enum(['duration', 'until']).default('duration'),
    /** Milliseconds (duration mode), up to 30 days. */
    durationMs: z
      .number()
      .int()
      .min(0)
      .max(30 * 86_400_000)
      .optional(),
    /** An ISO date-time or a template that renders one (until mode). */
    until: workflowTemplateSchema.optional(),
  })
  .refine((e) => (e.mode === 'duration' ? e.durationMs !== undefined : Boolean(e.until)), {
    message: 'Set how long to wait (or until when)',
  });
const parallelEntry = z.object({
  ...containerBase,
  type: z.literal('parallel'),
  steps: z.array(workflowContainerStepSchema).min(2, 'Add at least two steps').max(10),
});
const conditionalEntry = z.object({
  ...containerBase,
  type: z.literal('conditional'),
  /** Every branch whose condition holds runs (concurrently), like Mastra's `.branch()`. */
  branches: z
    .array(z.object({ condition: conditionSchema, step: workflowContainerStepSchema }))
    .min(1, 'Add a branch')
    .max(10),
  /** Runs when no branch matches. */
  fallback: workflowContainerStepSchema.optional(),
});
const loopEntry = z.object({
  ...containerBase,
  type: z.literal('loop'),
  /** `while`: repeat while the condition holds (do-while); `until`: repeat until it holds. */
  mode: z.enum(['while', 'until']).default('until'),
  condition: conditionSchema,
  step: workflowContainerStepSchema,
  maxIterations: z.number().int().min(1).max(100).default(10),
});
const foreachEntry = z.object({
  ...containerBase,
  type: z.literal('foreach'),
  /** The array to go through (default: the previous output). */
  items: workflowTemplateSchema.default('{{prev}}'),
  step: workflowContainerStepSchema,
  concurrency: z.number().int().min(1).max(10).default(1),
});

export const workflowEntrySchema = z.union([
  workflowStepSchema,
  sleepEntry,
  parallelEntry,
  conditionalEntry,
  loopEntry,
  foreachEntry,
]);
export type WorkflowEntry = z.infer<typeof workflowEntrySchema>;
export type WorkflowEntryType = WorkflowEntry['type'];
export type SleepEntry = z.infer<typeof sleepEntry>;
export type ParallelEntry = z.infer<typeof parallelEntry>;
export type ConditionalEntry = z.infer<typeof conditionalEntry>;
export type LoopEntry = z.infer<typeof loopEntry>;
export type ForeachEntry = z.infer<typeof foreachEntry>;
export const CONTAINER_TYPES = ['sleep', 'parallel', 'conditional', 'loop', 'foreach'] as const;
export const isLeaf = (entry: WorkflowEntry): entry is WorkflowStep =>
  !(CONTAINER_TYPES as readonly string[]).includes(entry.type);

/** The leaf steps directly inside an entry (or the entry itself when it is a leaf). */
export function entrySteps(entry: WorkflowEntry): WorkflowStep[] {
  switch (entry.type) {
    case 'parallel':
      return entry.steps;
    case 'conditional':
      return [...entry.branches.map((b) => b.step), ...(entry.fallback ? [entry.fallback] : [])];
    case 'loop':
    case 'foreach':
      return [entry.step];
    case 'sleep':
      return [];
    default:
      return [entry];
  }
}

/** Every id in the graph (entries and their steps), in order. */
export function graphIds(graph: WorkflowEntry[]): string[] {
  return graph.flatMap((e) => [...(isLeaf(e) ? [] : [e.id]), ...entrySteps(e).map((s) => s.id)]);
}

/** Finds an entry or step by id, with the top-level entry that contains it. */
export function findInGraph(
  graph: WorkflowEntry[],
  id: string,
): { entry: WorkflowEntry; index: number; step: WorkflowEntry | WorkflowStep } | null {
  for (const [index, entry] of graph.entries()) {
    if (entry.id === id) return { entry, index, step: entry };
    const step = entrySteps(entry).find((s) => s.id === id);
    if (step) return { entry, index, step };
  }
  return null;
}

// --- Definition ---

const definitionBody = z.object({
  version: z.literal(2).default(2),
  inputs: z.array(workflowInputSchema).max(30).default([]),
  graph: z.array(workflowEntrySchema).min(1, 'Add at least one step').max(50),
  /** The workflow result; defaults to the last entry's output. */
  output: workflowTemplateSchema.optional(),
});

/** v1 definitions (`steps: [...]`) become v2 (`graph: [...]`); their steps are all leaves. */
function migrateDefinition(value: unknown): unknown {
  if (!value || typeof value !== 'object') return value;
  const v = value as Record<string, unknown>;
  if (!('graph' in v) && Array.isArray(v.steps)) {
    const { steps, ...rest } = v;
    return { ...rest, version: 2, graph: steps };
  }
  return value;
}

export const workflowDefinitionSchema = z
  .preprocess(migrateDefinition, definitionBody)
  .superRefine((def, ctx) => validateGraph(def as WorkflowDefinition, ctx));
export type WorkflowDefinition = z.infer<typeof definitionBody>;

/** Parses a stored definition (v1 or v2) into v2. */
export function normalizeDefinition(value: unknown): WorkflowDefinition {
  return workflowDefinitionSchema.parse(value);
}

type Issue = { path: (string | number)[]; message: string };

/** References a template may use at a point in the graph. */
interface Visible {
  steps: Set<string>;
  inputs: Set<string>;
  item: boolean;
  iteration: boolean;
}

function checkTemplates(
  texts: string[],
  visible: Visible,
  path: (string | number)[],
  out: Issue[],
) {
  for (const ref of texts.flatMap((t) => templateRefs(t))) {
    if (ref.ns === 'steps' && !visible.steps.has(ref.key))
      out.push({ path, message: `“${ref.key}” is not an earlier step` });
    else if (ref.ns === 'input' && !visible.inputs.has(ref.key))
      out.push({ path, message: `“${ref.key}” is not a workflow input` });
    else if ((ref.ns === 'item' || ref.ns === 'index') && !visible.item)
      out.push({ path, message: `{{${ref.ns}}} is only available inside a foreach` });
    else if (ref.ns === 'iteration' && !visible.iteration)
      out.push({ path, message: '{{iteration}} is only available inside a loop' });
  }
}

const conditionTexts = (c: Condition | undefined) =>
  c ? c.rules.flatMap((r) => [r.left, r.right]) : [];

/** Templates of a leaf step (without its `when`). */
export function leafTemplates(step: WorkflowStep): string[] {
  switch (step.type) {
    case 'agent':
      return [step.prompt];
    case 'tool':
      return Object.values(step.args);
    case 'sandbox':
      return [];
    case 'transform':
      return [step.output];
    case 'workflow':
      return Object.values(step.input);
    case 'human':
      return [step.message];
  }
}

/** Every template an entry uses, conditions included (for validation and reference lists). */
export function stepTemplates(entry: WorkflowEntry): string[] {
  switch (entry.type) {
    case 'sleep':
      return entry.until ? [entry.until] : [];
    case 'parallel':
      return entry.steps.flatMap((s) => [...leafTemplates(s), ...conditionTexts(s.when)]);
    case 'conditional':
      return [
        ...entry.branches.flatMap((b) => [
          ...conditionTexts(b.condition),
          ...leafTemplates(b.step),
          ...conditionTexts(b.step.when),
        ]),
        ...(entry.fallback ? leafTemplates(entry.fallback) : []),
      ];
    case 'loop':
      return [...conditionTexts(entry.condition), ...leafTemplates(entry.step)];
    case 'foreach':
      return [entry.items, ...leafTemplates(entry.step)];
    default:
      return [...leafTemplates(entry), ...conditionTexts(entry.when)];
  }
}

function validateGraph(def: WorkflowDefinition, ctx: z.RefinementCtx) {
  const issues: Issue[] = [];
  const inputs = new Set<string>();
  def.inputs.forEach((input, i) => {
    if (inputs.has(input.name))
      issues.push({ path: ['inputs', i, 'name'], message: 'Duplicate input' });
    inputs.add(input.name);
  });
  const ids = new Set<string>();
  const claim = (id: string, path: (string | number)[]) => {
    if (ids.has(id)) issues.push({ path, message: 'Step ids must be unique' });
    ids.add(id);
  };
  const seen = new Set<string>();
  const visible = (extra: Partial<Visible> = {}): Visible => ({
    steps: new Set(seen),
    inputs,
    item: false,
    iteration: false,
    ...extra,
  });
  def.graph.forEach((entry, i) => {
    const at = ['graph', i];
    claim(entry.id, [...at, 'id']);
    switch (entry.type) {
      case 'sleep':
        checkTemplates(entry.until ? [entry.until] : [], visible(), at, issues);
        break;
      case 'parallel':
        entry.steps.forEach((s, j) => {
          claim(s.id, [...at, 'steps', j, 'id']);
          checkTemplates(
            [...leafTemplates(s), ...conditionTexts(s.when)],
            visible(),
            [...at, 'steps', j],
            issues,
          );
        });
        break;
      case 'conditional':
        entry.branches.forEach((b, j) => {
          claim(b.step.id, [...at, 'branches', j, 'step', 'id']);
          checkTemplates(
            conditionTexts(b.condition),
            visible(),
            [...at, 'branches', j, 'condition'],
            issues,
          );
          checkTemplates(
            [...leafTemplates(b.step), ...conditionTexts(b.step.when)],
            visible(),
            [...at, 'branches', j, 'step'],
            issues,
          );
        });
        if (entry.fallback) {
          claim(entry.fallback.id, [...at, 'fallback', 'id']);
          checkTemplates(leafTemplates(entry.fallback), visible(), [...at, 'fallback'], issues);
        }
        break;
      case 'loop':
        claim(entry.step.id, [...at, 'step', 'id']);
        checkTemplates(
          [...leafTemplates(entry.step), ...conditionTexts(entry.step.when)],
          visible({ iteration: true }),
          [...at, 'step'],
          issues,
        );
        // The condition is checked after each iteration: it can read the step's output.
        checkTemplates(
          conditionTexts(entry.condition),
          { ...visible({ iteration: true }), steps: new Set([...seen, entry.step.id]) },
          [...at, 'condition'],
          issues,
        );
        break;
      case 'foreach':
        claim(entry.step.id, [...at, 'step', 'id']);
        checkTemplates([entry.items], visible(), [...at, 'items'], issues);
        checkTemplates(
          [...leafTemplates(entry.step), ...conditionTexts(entry.step.when)],
          visible({ item: true }),
          [...at, 'step'],
          issues,
        );
        break;
      default:
        checkTemplates(stepTemplates(entry), visible(), at, issues);
    }
    for (const id of [entry.id, ...entrySteps(entry).map((s) => s.id)]) seen.add(id);
  });
  checkTemplates(def.output ? [def.output] : [], visible(), ['output'], issues);
  for (const issue of issues)
    ctx.addIssue({ code: 'custom', path: issue.path, message: issue.message });
}

// --- Templates ---

const NAMESPACES = 'input|context|steps|prev|item|index|iteration';
const WORKFLOW_PLACEHOLDER = new RegExp(
  `\\{\\{\\s*(${NAMESPACES})((?:\\.[A-Za-z0-9_]+)*)\\s*\\}\\}`,
  'g',
);
const WHOLE_PLACEHOLDER = new RegExp(
  `^\\{\\{\\s*(${NAMESPACES})((?:\\.[A-Za-z0-9_]+)*)\\s*\\}\\}$`,
);

export interface TemplateRef {
  ns: string;
  /** The first path segment (input name, step id …); empty for `{{prev}}`, `{{item}}` … */
  key: string;
  /** The rest of the path. */
  path: string[];
}

export function templateRefs(template: string): TemplateRef[] {
  return [...template.matchAll(WORKFLOW_PLACEHOLDER)]
    .map((m) => {
      const segments = m[2] ? m[2].slice(1).split('.') : [];
      return { ns: m[1]!, key: segments[0] ?? '', path: segments.slice(1) };
    })
    .filter((r) => !['input', 'context', 'steps'].includes(r.ns) || r.key !== '');
}

export interface WorkflowScope {
  input: Record<string, unknown>;
  context: Record<string, unknown>;
  steps: Record<string, { output: unknown }>;
  /** The previous entry's output. */
  prev?: unknown;
  /** The current foreach item and its index. */
  item?: unknown;
  index?: number;
  /** The current loop iteration (1-based). */
  iteration?: number;
}

/** Reads `path` from a value; JSON text is parsed on the way so `.field` works on agent answers. */
function walk(value: unknown, path: string[]): unknown {
  let current = value;
  for (const segment of path) {
    if (current === null || current === undefined) return undefined;
    if (typeof current === 'string') {
      try {
        current = JSON.parse(current);
      } catch {
        return undefined;
      }
    }
    if (typeof current !== 'object') return undefined;
    current = Object.prototype.hasOwnProperty.call(current, segment)
      ? (current as Record<string, unknown>)[segment]
      : undefined;
  }
  return current;
}

function resolveRef(scope: WorkflowScope, ns: string, segments: string[]): unknown {
  switch (ns) {
    case 'input':
    case 'context': {
      const root = scope[ns];
      const [key, ...rest] = segments;
      if (!key || !Object.prototype.hasOwnProperty.call(root, key)) return undefined;
      return walk(root[key], rest);
    }
    case 'steps': {
      const [key, ...rest] = segments;
      if (!key || !Object.prototype.hasOwnProperty.call(scope.steps, key)) return undefined;
      return walk(scope.steps[key], rest);
    }
    case 'prev':
      return walk(scope.prev, segments);
    case 'item':
      return walk(scope.item, segments);
    case 'index':
      return segments.length ? undefined : scope.index;
    case 'iteration':
      return segments.length ? undefined : scope.iteration;
    default:
      return undefined;
  }
}

const asText = (value: unknown) =>
  value === undefined || value === null
    ? ''
    : typeof value === 'string'
      ? value
      : JSON.stringify(value);

/** Renders a template to text (missing values render as empty). */
export function renderWorkflowTemplate(template: string, scope: WorkflowScope): string {
  return template.replace(WORKFLOW_PLACEHOLDER, (_m, ns: string, rest: string) =>
    asText(resolveRef(scope, ns, rest ? rest.slice(1).split('.') : [])),
  );
}

/** Like `renderWorkflowTemplate`, but a template that is exactly one placeholder keeps its type. */
export function resolveWorkflowValue(template: string, scope: WorkflowScope): unknown {
  const whole = WHOLE_PLACEHOLDER.exec(template.trim());
  if (whole)
    return resolveRef(scope, whole[1]!, whole[2] ? whole[2].slice(1).split('.') : []) ?? null;
  return renderWorkflowTemplate(template, scope);
}

const isEmpty = (text: string) => text === '' || text === 'null' || text === '[]' || text === '{}';

export function evaluateRule(rule: ConditionRule, scope: WorkflowScope): boolean {
  const leftText = asText(resolveWorkflowValue(rule.left, scope)).trim();
  const right = renderWorkflowTemplate(rule.right, scope).trim();
  const numbers = () => [Number(leftText), Number(right)] as const;
  switch (rule.operator) {
    case 'equals':
      return leftText === right;
    case 'not_equals':
      return leftText !== right;
    case 'contains':
      return leftText.toLowerCase().includes(right.toLowerCase());
    case 'not_contains':
      return !leftText.toLowerCase().includes(right.toLowerCase());
    case 'is_empty':
      return isEmpty(leftText);
    case 'not_empty':
      return !isEmpty(leftText);
    case 'greater_than': {
      const [a, b] = numbers();
      return a > b;
    }
    case 'greater_or_equal': {
      const [a, b] = numbers();
      return a >= b;
    }
    case 'less_than': {
      const [a, b] = numbers();
      return a < b;
    }
    case 'less_or_equal': {
      const [a, b] = numbers();
      return a <= b;
    }
  }
}

/** Evaluates a condition (or an older single rule). */
export function evaluateCondition(
  condition: Condition | ConditionRule,
  scope: WorkflowScope,
): boolean {
  const group: Condition = 'rules' in condition ? condition : { match: 'all', rules: [condition] };
  return group.match === 'all'
    ? group.rules.every((r) => evaluateRule(r, scope))
    : group.rules.some((r) => evaluateRule(r, scope));
}

const OPERATOR_SYMBOLS: Record<ConditionOperator, string> = {
  equals: '=',
  not_equals: '≠',
  contains: 'contains',
  not_contains: 'excludes',
  is_empty: 'is empty',
  not_empty: 'is set',
  greater_than: '>',
  greater_or_equal: '≥',
  less_than: '<',
  less_or_equal: '≤',
};

/** A short label for graph edges: `steps.check.output.ok = true`. */
export function conditionLabel(condition: Condition, max = 60): string {
  const strip = (t: string) => t.replace(/\{\{\s*|\s*\}\}/g, '');
  const label = condition.rules
    .map((r) =>
      ['is_empty', 'not_empty'].includes(r.operator)
        ? `${strip(r.left)} ${OPERATOR_SYMBOLS[r.operator]}`
        : `${strip(r.left)} ${OPERATOR_SYMBOLS[r.operator]} ${strip(r.right) || '""'}`,
    )
    .join(condition.match === 'all' ? ' and ' : ' or ');
  return label.length > max ? `${label.slice(0, max - 1)}…` : label;
}

// --- API: workflows ---

export const createWorkflowRequestSchema = z.object({
  name: resourceNameSchema,
  description: z.string().trim().max(2000).default(''),
  definition: workflowDefinitionSchema,
});
export type CreateWorkflowRequest = z.infer<typeof createWorkflowRequestSchema>;

export const updateWorkflowRequestSchema = z
  .object({
    description: z.string().trim().max(2000).optional(),
    definition: workflowDefinitionSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateWorkflowRequest = z.infer<typeof updateWorkflowRequestSchema>;

/**
 * `suspended`: a human step waits for input; `waiting`: a sleep; `paused`: step-by-step mode
 * waits for "Run next step". The other three are final.
 */
export const workflowRunStatusSchema = z.enum([
  'running',
  'succeeded',
  'failed',
  'cancelled',
  'suspended',
  'waiting',
  'paused',
]);
export type WorkflowRunStatus = z.infer<typeof workflowRunStatusSchema>;
export const FINAL_WORKFLOW_STATUSES: WorkflowRunStatus[] = ['succeeded', 'failed', 'cancelled'];

export const workflowStepStatusSchema = z.enum([
  'pending',
  'running',
  'succeeded',
  'failed',
  'skipped',
  'suspended',
  'waiting',
  'cancelled',
]);
export type WorkflowStepStatus = z.infer<typeof workflowStepStatusSchema>;

export const workflowTriggerSchema = z.enum(['console', 'agent', 'schedule', 'workflow']);
export type WorkflowTrigger = z.infer<typeof workflowTriggerSchema>;

export const workflowStepResultSchema = z.object({
  stepId: z.string(),
  type: z.string(),
  name: z.string(),
  status: workflowStepStatusSchema,
  startedAt: isoTimestamp.nullable(),
  endedAt: isoTimestamp.nullable(),
  /** The rendered input (agent message, tool args, nested input …). */
  input: z.unknown().optional(),
  output: z.unknown().optional(),
  error: z.string().nullable(),
  /** The agent run of an agent step (a trace of its own). */
  runId: z.uuid().nullable(),
  /** The run of a nested workflow step. */
  childRunId: z.uuid().nullable().optional(),
  /** Loops: iterations run. Foreach: items done / total. */
  iterations: z.number().int().optional(),
  items: z.object({ total: z.number().int(), done: z.number().int() }).optional(),
  /** Sleeps: when the run continues. */
  resumeAt: isoTimestamp.optional(),
  /** Human steps: the rendered message. */
  message: z.string().optional(),
});
export type WorkflowStepResult = z.infer<typeof workflowStepResultSchema>;

/**
 * A definition as responses carry it (always v2, already normalized). Responses are encoded, and
 * the request schema's v1 migration cannot run in reverse.
 */
const definitionOutputSchema = z.custom<WorkflowDefinition>(
  (v) => typeof v === 'object' && v !== null && Array.isArray((v as { graph?: unknown }).graph),
);

export const workflowDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  definition: definitionOutputSchema,
  lastRun: z
    .object({ id: z.uuid(), status: workflowRunStatusSchema, startedAt: isoTimestamp })
    .nullable(),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});
export type WorkflowDto = z.infer<typeof workflowDtoSchema>;

export const workflowRunDtoSchema = z.object({
  id: z.uuid(),
  workflowId: z.uuid(),
  workflowName: z.string(),
  status: workflowRunStatusSchema,
  trigger: workflowTriggerSchema,
  input: z.record(z.string(), z.unknown()),
  requestContext: z.record(z.string(), z.unknown()),
  output: z.unknown().nullable(),
  error: z.string().nullable(),
  /** Results in graph order (entries and their steps). */
  steps: z.array(workflowStepResultSchema),
  /** The definition the run uses (snapshotted at start). */
  definition: definitionOutputSchema,
  debug: z.boolean(),
  /** The human step waiting for input. */
  suspendedStepId: z.string().nullable(),
  resumeAt: isoTimestamp.nullable(),
  parentRunId: z.uuid().nullable(),
  /** Set when the run re-ran another one from a step. */
  timeTravel: z.object({ fromRunId: z.uuid(), stepId: z.string() }).nullable(),
  userId: z.uuid().nullable(),
  startedAt: isoTimestamp,
  endedAt: isoTimestamp.nullable(),
  latencyMs: z.number().int().nullable(),
});
export type WorkflowRunDto = z.infer<typeof workflowRunDtoSchema>;

export const startWorkflowRequestSchema = z.object({
  input: z.record(z.string(), z.unknown()).default({}),
  requestContext: requestContextValuesSchema.default({}),
  /** Step by step: pause after every entry until "Run next step". */
  debug: z.boolean().default(false),
  /**
   * The console's unsaved draft: run it instead of the saved definition (it is not saved; the
   * run records it). Checked like a save.
   */
  definition: workflowDefinitionSchema.optional(),
});
export type StartWorkflowRequest = z.infer<typeof startWorkflowRequestSchema>;

export const resumeWorkflowRequestSchema = z.object({
  /** Values for the human step's fields. */
  data: z.record(z.string(), z.unknown()).default({}),
});
export type ResumeWorkflowRequest = z.infer<typeof resumeWorkflowRequestSchema>;

export const timeTravelRequestSchema = z.object({
  /** The step (or entry) to start from; everything before it is reused from the run. */
  stepId: workflowStepIdSchema,
  /** Replaces the rendered input of that step (e.g. an edited agent message or tool args). */
  stepInput: z.unknown().optional(),
  /** Replaces the workflow input. */
  input: z.record(z.string(), z.unknown()).optional(),
  debug: z.boolean().default(false),
});
export type TimeTravelRequest = z.infer<typeof timeTravelRequestSchema>;

export const workflowRunListQuerySchema = z.object({
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  workflowId: z.uuid().optional(),
  status: workflowRunStatusSchema.optional(),
  trigger: workflowTriggerSchema.optional(),
});
export type WorkflowRunListQuery = z.infer<typeof workflowRunListQuerySchema>;

export const workflowRunCountsSchema = z.record(
  z.uuid(),
  z.object({
    total: z.number().int(),
    running: z.number().int(),
    succeeded: z.number().int(),
    failed: z.number().int(),
    suspended: z.number().int(),
  }),
);
export type WorkflowRunCounts = z.infer<typeof workflowRunCountsSchema>;

/** Server-sent events of a workflow run. */
export const workflowEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('workflow.started'), runId: z.uuid() }),
  z.object({ type: z.literal('step.started'), stepId: z.string(), input: z.unknown() }),
  z.object({ type: z.literal('step.delta'), stepId: z.string(), text: z.string() }),
  z.object({
    type: z.literal('step.progress'),
    stepId: z.string(),
    iterations: z.number().int().optional(),
    items: z.object({ total: z.number().int(), done: z.number().int() }).optional(),
  }),
  z.object({
    type: z.literal('step.completed'),
    stepId: z.string(),
    output: z.unknown(),
    runId: z.uuid().nullable(),
  }),
  z.object({ type: z.literal('step.skipped'), stepId: z.string() }),
  z.object({
    type: z.literal('step.failed'),
    stepId: z.string(),
    error: z.string(),
    runId: z.uuid().nullable(),
  }),
  z.object({
    type: z.literal('step.suspended'),
    stepId: z.string(),
    message: z.string(),
    fields: z.array(workflowInputSchema),
  }),
  z.object({ type: z.literal('step.waiting'), stepId: z.string(), resumeAt: isoTimestamp }),
  z.object({
    type: z.literal('workflow.completed'),
    runId: z.uuid(),
    /** Final, or where the run stopped for now (suspended, waiting, paused). */
    status: workflowRunStatusSchema,
    output: z.unknown().nullable(),
    error: z.string().nullable(),
    latencyMs: z.number().int(),
  }),
]);
export type WorkflowEventDto = z.infer<typeof workflowEventSchema>;

// --- Schedules ---

export const createWorkflowScheduleRequestSchema = z.object({
  name: z.string().trim().max(100).default(''),
  cron: cronExpressionSchema,
  timezone: timeZoneSchema.default('UTC'),
  input: z.record(z.string(), z.unknown()).default({}),
  requestContext: requestContextValuesSchema.default({}),
  enabled: z.boolean().default(true),
});
export type CreateWorkflowScheduleRequest = z.infer<typeof createWorkflowScheduleRequestSchema>;

export const updateWorkflowScheduleRequestSchema = z
  .object({
    name: z.string().trim().max(100).optional(),
    cron: cronExpressionSchema.optional(),
    timezone: timeZoneSchema.optional(),
    input: z.record(z.string(), z.unknown()).optional(),
    requestContext: requestContextValuesSchema.optional(),
    enabled: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateWorkflowScheduleRequest = z.infer<typeof updateWorkflowScheduleRequestSchema>;

export const workflowScheduleDtoSchema = z.object({
  id: z.uuid(),
  workflowId: z.uuid(),
  name: z.string(),
  cron: z.string(),
  timezone: z.string(),
  input: z.record(z.string(), z.unknown()),
  requestContext: z.record(z.string(), z.unknown()),
  enabled: z.boolean(),
  nextRunAt: isoTimestamp.nullable(),
  lastRunAt: isoTimestamp.nullable(),
  lastRunId: z.uuid().nullable(),
  lastError: z.string().nullable(),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});
export type WorkflowScheduleDto = z.infer<typeof workflowScheduleDtoSchema>;
